/**
 * Sync Details Dialog
 *
 * Modal dialog showing detailed information about a sync operation
 */

import React from 'react'
import {
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Paper
} from '@mui/material'
import { SyncOperation } from '../../../../services/types/sync'
import { getSystemIcon } from '../../../../components/SystemIcons'
import { formatDateTime } from '../../../../utils/inspectorFormatters'
import BaseDialog from '../../../../components/shared/dialogs/BaseDialog'

interface SyncDetailsDialogProps {
  open: boolean
  onClose: () => void
  operation: SyncOperation | null
}

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'error'
    case 'cancelled':
      return 'warning'
    case 'running':
      return 'info'
    case 'pending':
      return 'default'
    default:
      return 'default'
  }
}

const formatDuration = (seconds?: number): string => {
  if (!seconds && seconds !== 0) return '-'
  if (seconds < 60) return `${Math.round(seconds)} seconds`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 0.75 }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ width: 140, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1 }}>
      {value || '-'}
    </Typography>
  </Box>
)

export const SyncDetailsDialog: React.FC<SyncDetailsDialogProps> = ({
  open,
  onClose,
  operation
}) => {
  if (!operation) return null

  const hasMetadata = operation.sync_metadata && Object.keys(operation.sync_metadata).length > 0

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Sync Operation Details"
      subtitle={operation.operation_id}
      variant="info"
      size="md"
      headerAdornment={
        <>
          {getSystemIcon(operation.sync_system, { size: 20 })}
          <Chip
            label={operation.status}
            color={getStatusColor(operation.status)}
            sx={{ textTransform: 'capitalize' }}
          />
        </>
      }
      footer={<Button onClick={onClose}>Close</Button>}
    >
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <DetailRow label="Franchise" value={`${operation.fp_code?.toUpperCase()} - ${operation.franchise_name}`} />
              <DetailRow label="System" value={operation.sync_system} />
              <DetailRow label="Operation Type" value={operation.operation_type} />
              <DetailRow label="Initiated By" value={operation.initiated_by || 'System'} />
            </Paper>
          </Grid>

          {/* Timing */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Timing
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <DetailRow label="Started" value={operation.started_at ? formatDateTime(operation.started_at) : '-'} />
              <DetailRow label="Completed" value={operation.completed_at ? formatDateTime(operation.completed_at) : '-'} />
              <DetailRow label="Duration" value={formatDuration(operation.duration_seconds)} />
              <DetailRow label="Progress" value={`${operation.progress_percentage || 0}%`} />
            </Paper>
          </Grid>

          {/* Records */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Records
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <DetailRow label="Total Processed" value={operation.records_processed?.toLocaleString() || '0'} />
              <DetailRow label="Created" value={operation.records_created?.toLocaleString() || '0'} />
              <DetailRow label="Updated" value={operation.records_updated?.toLocaleString() || '0'} />
              <DetailRow label="Deleted" value={operation.records_deleted?.toLocaleString() || '0'} />
            </Paper>
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Status Details
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <DetailRow label="Current Step" value={operation.current_step} />
              {operation.error_message && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                    Error:
                  </Typography>
                  <Typography variant="body2" color="error" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {operation.error_message}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Sync Metadata (Entity Breakdown) */}
          {hasMetadata && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Entity Breakdown
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {Object.entries(operation.sync_metadata!).map(([system, dataTypes]) => (
                    <Grid item xs={12} key={system}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textTransform: 'capitalize' }}>
                        {system}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(dataTypes).map(([dataType, counts]: [string, any]) => (
                          <Chip
                            key={dataType}
                            size="small"
                            variant="outlined"
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {counts.emoji && <span>{counts.emoji}</span>}
                                <span>{counts.display_name || dataType}</span>
                                <span style={{ fontWeight: 600 }}>
                                  {counts.created > 0 && ` +${counts.created}`}
                                  {counts.updated > 0 && ` ~${counts.updated}`}
                                  {counts.deleted > 0 && ` -${counts.deleted}`}
                                </span>
                              </Box>
                            }
                          />
                        ))}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
    </BaseDialog>
  )
}

export default SyncDetailsDialog
